import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { StarIcon, CheckCircleIcon } from '@heroicons/react/24/solid'
import {
  ShipmentGroup,
  ShipmentGroupErrors,
  ShipmentPieceRow,
} from '../../interfaces/Import/ShipmentGroupInterface'
import { DeliveryEnum } from '../../interfaces'
import { ServiceDTO } from '../../interfaces/Services/Services'
import {
  FormText,
  FormSelect,
  FormTextArea,
  FormSearch,
  ShipmentBulkUploadPiecesList,
  FavoriteDestinationPicker,
} from '..'
import {
  FormMapsAutocomplete,
  AutocompleteRegion,
} from '../FormFields/FormMaps'
import { useAppSelector } from '../../store/hooks'
import { getStoreByCoverageDjango } from '../../services'
import { getShipmentsExisting } from '../../services/ShipmentBulkUploadServices'
import { DestinationInterface } from '../../interfaces/Account/DestinationInterface'
import {
  applyDestinationFromFavorite,
  changeCountryCode,
  clearConsigneeFields,
  reverseGeocodeCoords,
  splitPhoneCountryCode,
  tryParsePastedPhone,
  COORD_OUT_OF_ZONE_MSG,
} from '../../utils/shipmentImportUtils'
import {
  composeConsigneePhone,
  consigneeCountryCode,
  PHONE_COUNTRY_CODE,
  PHONE_MAX_LOCAL_DIGITS,
  VE_LOCAL_DIGITS,
} from '../../utils/shipmentValidation'
import { CountryPhoneOption } from '../../interfaces/General/CountryPhoneDto'
import {
  FALLBACK_COUNTRY_OPTIONS,
  filterCountryOption,
  findCountryByDialCode,
} from '../../services/GeneralServices'
import { obtenerCodigoArea, obtenerNumero } from '../../utils/format'
import { isExecutionErrorResolved } from '../../utils/shipmentBulkUploadStatus'
import { isJuridicIdentification } from '../../utils/shipmentValidation'

type OptionLike = {
  id?: any
  name?: string
  value?: any
  locationCode?: string
  locationName?: string
  abbreviationName?: string
  taxIdentificationTypeId?: any
  buCode?: string
  buName?: string
  location?: {
    name?: string
    locationCode?: string
    address?: string
    postalCode?: string
  }
}

// Module-scope filter helper (no closure deps) — stable reference across renders,
// safe to pass as `onFilter` prop without recreating per-row.
const filterByName = (opt: OptionLike, filter: string) => {
  const q = filter.toLowerCase()
  return (
    (opt.name || '').toLowerCase().includes(q) ||
    (opt.abbreviationName || '').toLowerCase().includes(q) ||
    (opt.locationName || '').toLowerCase().includes(q) ||
    (opt.buCode || '').toLowerCase().includes(q) ||
    (opt.buName || '').toLowerCase().includes(q)
  )
}

export const ShipmentBulkUploadAccordionRow: FC<{
  group: ShipmentGroup
  index: number
  errors: ShipmentGroupErrors
  identificationTypes: OptionLike[]
  typeShipment: { value: string; name: string }[]
  /** Catálogo COMPLETO: resuelve/muestra la tienda ya asignada, aunque el contrato
   *  no la permita (mejor verla y poder corregirla que dejar el campo vacío). */
  businessUnits: OptionLike[]
  /** Tiendas destino SELECCIONABLES. Las resuelve el Review con el contrato
   *  corporativo (AccountSiteBU); si no viene, se ofrece el catálogo completo. */
  destinationOptions?: OptionLike[]
  cities: OptionLike[]
  citiesByBu?: { locationCode: string; buCode: string }[]
  pieceCategories: OptionLike[]
  isCOD: boolean
  serviceOptions?: ServiceDTO[]
  onChangeGroup: (patch: Partial<ShipmentGroup>) => void
  onChangePiece: (pieceIndex: number, patch: Partial<ShipmentPieceRow>) => void
  onAddPiece: () => void
  onRemovePiece: (pieceIndex: number) => void
  validatePiece: (
    p: ShipmentPieceRow,
    serviceId?: number,
    groupPieces?: ShipmentPieceRow[],
    replaceIndex?: number
  ) => Partial<Record<keyof ShipmentPieceRow, string>>
  onRequestMerge?: () => void
  onCollapsed?: () => void
  open?: boolean
  onToggle?: () => void
  phonePrefixList?: { value: string; text: string }[]
  /** Catálogo de países para el teléfono del destinatario (GET /Country). */
  countryOptions?: CountryPhoneOption[]
  /** El catálogo de países no cargó: se avisa bajo el campo y solo se ofrece +58. */
  countryCatalogFailed?: boolean
  favoriteDestinations?: DestinationInterface[]
  favsByRif?: Map<string, DestinationInterface>
  /** Muestra el checkbox de selección múltiple. Off por defecto: la fila se sigue
   *  usando en el flujo de corrección, donde no hay acciones masivas. */
  selectable?: boolean
  selected?: boolean
  onToggleSelected?: () => void
}> = ({
  group,
  index,
  errors,
  identificationTypes,
  typeShipment,
  businessUnits,
  destinationOptions,
  cities,
  citiesByBu = [],
  pieceCategories,
  isCOD,
  serviceOptions = [],
  onChangeGroup,
  onChangePiece,
  onAddPiece,
  onRemovePiece,
  validatePiece,
  onRequestMerge,
  onCollapsed,
  open: controlledOpen,
  onToggle,
  phonePrefixList = [],
  countryOptions = [],
  countryCatalogFailed = false,
  favoriteDestinations = [],
  favsByRif,
  selectable = false,
  selected = false,
  onToggleSelected,
}) => {
  const isDup = group.isDuplicateInDB
  const hasError = Object.keys(errors).length > 0 || isDup
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const dbCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Solo re-consultamos la BD cuando el operador EDITA manualmente el número de guía.
  // En la carga inicial el `isDuplicateInDB` ya viene resuelto por el chequeo batcheado
  // (lotes de 5000, POST /shipment/existing) de handleFileSubmit, así que NO disparamos
  // un llamado por fila al montar: con N guías serían N requests innecesarios (100k guías
  // → 100k llamadas). Arranca en false; solo el onChange del input lo activa.
  const userEditedRef = useRef(false)
  const user = useAppSelector((s) => s.user)

  useEffect(() => {
    if (!userEditedRef.current) return
    const tracking = group.shipmentHeaderReference.trim()
    if (dbCheckTimer.current) clearTimeout(dbCheckTimer.current)
    if (!tracking) {
      onChangeGroup({ isDuplicateInDB: false })
      return
    }
    dbCheckTimer.current = setTimeout(async () => {
      try {
        const accountSiteCode = user.client?.accountCode ?? ''
        const accountId = user.client?.id ?? ''
        const accountScope: {
          account_code?: string
          shipper_account_id?: string
        } = accountSiteCode
          ? { account_code: accountSiteCode }
          : accountId
            ? { shipper_account_id: accountId }
            : {}
        const res = await getShipmentsExisting({
          shipment_identifiers: [tracking],
          ...accountScope,
        })
        const exists = (res.model?.existing_shipment_identifiers ?? []).some(
          (s) => String(s).trim().toUpperCase() === tracking.toUpperCase()
        )
        onChangeGroup({ isDuplicateInDB: exists })
      } catch (_) {}
    }, 500)
    return () => {
      if (dbCheckTimer.current) clearTimeout(dbCheckTimer.current)
    }
  }, [group.shipmentHeaderReference, user.client?.accountCode, user.client?.id])

  const consigneeDocTypeObj = useMemo(
    () =>
      identificationTypes.find(
        (t) => t.taxIdentificationTypeId?.toString() === group.consigneeDocType
      ),
    [identificationTypes, group.consigneeDocType]
  )
  const shipperDocTypeObj = useMemo(
    () =>
      identificationTypes.find(
        (t) => t.taxIdentificationTypeId?.toString() === group.shipperDocType
      ),
    [identificationTypes, group.shipperDocType]
  )
  const cityObj = useMemo(
    () => cities.find((c) => c.locationCode === group.destinationCity),
    [cities, group.destinationCity]
  )
  const buObj = useMemo(
    () =>
      businessUnits.find(
        (b) => String(b.buCode ?? '') === String(group.storeCode ?? '')
      ),
    [businessUnits, group.storeCode]
  )

  const [consigneeDocSearch, setConsigneeDocSearch] = useState(
    consigneeDocTypeObj?.abbreviationName || ''
  )
  const [shipperDocSearch, setShipperDocSearch] = useState(
    shipperDocTypeObj?.abbreviationName || ''
  )
  const [citySearch, setCitySearch] = useState(cityObj?.locationName || '')
  const [buSearch, setBuSearch] = useState(
    buObj
      ? `${buObj.buCode} - ${buObj.location?.name || buObj.buName || (buObj as any).buArea || ''}`
      : ''
  )
  const [deliverySearch, setDeliverySearch] = useState(
    typeShipment.find((t) => t.value === group.deliveryType)?.name || ''
  )

  // Oficinas origen del contrato (multi-oficina). Se lee directo del store (ya
  // está `user` en scope) para no prop-drillear.
  const originBusinessUnits = (user.originBusinessUnits ?? []) as OptionLike[]
  const originLabel = (b?: OptionLike) =>
    b ? `${b.buCode} - ${b.location?.name || b.buName || ''}` : ''
  const originBuObj = useMemo(
    () =>
      originBusinessUnits.find((b) => b.buCode === group.originStoreCode) ??
      (originBusinessUnits.length === 1 ? originBusinessUnits[0] : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [originBusinessUnits, group.originStoreCode]
  )
  // Texto del campo. Cuando la fila trae un buCode que el contrato NO habilita,
  // `originBuObj` queda undefined (multi-oficina): se muestra el código tal cual vino
  // del Excel en vez de dejar el campo vacío — el combo sale en rojo con "Oficina
  // origen no válida para este contrato" y así el operador ve QUÉ escribió mal.
  const originFieldValue = (b?: OptionLike) =>
    b ? originLabel(b) : (group.originStoreCode ?? '')
  const [originSearch, setOriginSearch] = useState(originFieldValue(originBuObj))
  // Refleja en el input la tienda origen que se haya asignado por fuera del combo:
  // la que trae el Excel y, sobre todo, la que aplica la barra de acciones masivas.
  // Sin esto la guía quedaba con originStoreCode seteado pero el campo en blanco.
  // Escribir en el buscador no lo dispara (ni originBuObj ni originStoreCode cambian
  // al tipear; originStoreCode solo se escribe al seleccionar una opción).
  useEffect(() => {
    setOriginSearch(originFieldValue(originBuObj))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originBuObj, group.originStoreCode])

  /**
   * La nota de rechazo se apaga SOLA cuando se puede verificar contra los datos
   * ACTUALES de la fila que el motivo ya se corrigió (nombre que ya tiene sus 2
   * palabras, nº de guía aliada que ya no es el duplicado…). La nota no se
   * limpia al editar la fila (los chequeos de montaje disparan `onChangeGroup`
   * solos y la borrarían antes de leerla), así que sin esta verificación seguía
   * acusando un error ya resuelto — tanto al corregir en vivo como al subir el
   * archivo corregido. Para motivos no verificables sigue haciendo falta el
   * "Cerrar" manual: ante la duda, el motivo se muestra.
   */
  const serverErrorResolved = useMemo(
    () =>
      !!group.serverErrorNote &&
      isExecutionErrorResolved(group.serverErrorNote, {
        shipperName: group.shipperName,
        consigneeName: group.consigneeName,
        consigneePhone: group.consigneePhone,
        consigneePhoneCountryCode: group.consigneePhoneCountryCode,
        shipmentHeaderReference: group.shipmentHeaderReference,
        destinationCity: group.destinationCity,
        storeCode: group.storeCode,
        isCOD,
        // Tipo de persona por parte: sin esto un nombre jurídico de una sola
        // palabra ("SHEIN") se re-valida con la regla natural y la nota de
        // rechazo nunca se apaga.
        shipperIsJuridic: isJuridicIdentification(
          shipperDocTypeObj?.abbreviationName
        ),
        consigneeIsJuridic: isJuridicIdentification(
          consigneeDocTypeObj?.abbreviationName
        ),
      }),
    [
      group.serverErrorNote,
      group.shipperName,
      group.consigneeName,
      group.consigneePhone,
      group.consigneePhoneCountryCode,
      group.shipmentHeaderReference,
      group.destinationCity,
      group.storeCode,
      isCOD,
      shipperDocTypeObj,
      consigneeDocTypeObj,
    ]
  )

  /**
   * El backend rechazó esta guía y el motivo no quedó anclado a ningún campo
   * (regla que el front no traduce, o campo que no se edita por fila). Se trata
   * como error a efectos de PINTAR la fila —si no, llegaba al editor en verde y
   * con el check de "guía válida"— pero no entra en `errors`, así que no bloquea
   * el envío: no hay campo que corregir y bloquear dejaría al operador sin salida.
   */
  const hasUnfixedServerError =
    !!group.serverErrorNote &&
    !serverErrorResolved &&
    Object.keys(errors).length === 0

  const headerBg = group.coordWarning
    ? 'bg-amber-50 border-amber-300'
    : hasError || hasUnfixedServerError
      ? 'bg-red-50 border-red-300'
      : 'bg-gray-50 border-gray-200'

  const errorBadge = group.coordWarning
    ? 'Coordenadas fuera de zona'
    : Object.keys(errors).length > 0
      ? `Con errores: ${Object.keys(errors).length}`
      : isDup
        ? 'Guía existente'
        : hasUnfixedServerError
          ? 'Rechazada por el sistema'
          : null

  const dupBadgeStyle = 'bg-red-100 text-red-700'

  const totalPieceCount = group.pieces.reduce(
    (s, p) => s + (p.quantity || 1),
    0
  )

  const isHome = group.deliveryType === DeliveryEnum.DELIVERY
  const isOffice = group.deliveryType === DeliveryEnum.OFFICE
  /**
   * ¿La tienda destino elegida aporta su dirección? Con ella, "Dirección destino"
   * es de solo lectura (la copia el select del código de tienda). Sin ella hay que
   * dejar escribir, o la fila queda trabada: campo vacío + deshabilitado + Requerido.
   * Se mira el catálogo COMPLETO (`businessUnits`), que es de donde sale `buObj`.
   */
  const storeProvidesAddress = !!buObj?.location?.address?.trim()
  const hasMapsCoords =
    isHome && group.latitude != null && group.longitude != null
  useEffect(() => {
    if (!group.storeCode) {
      setBuSearch('')
      return
    }
    const bu = businessUnits.find(
      (b) => String(b.buCode ?? '') === String(group.storeCode ?? '')
    )
    setBuSearch(
      bu
        ? `${bu.buCode} - ${bu.location?.name || bu.buName || (bu as any).buArea || ''}`
            .replace(/^ - /, '')
            .trim()
        : group.storeCode
    )
  }, [group.storeCode, businessUnits])

  useEffect(() => {
    if (!group.destinationCity) {
      setCitySearch('')
      return
    }
    const city = cities.find((c) => c.locationCode === group.destinationCity)
    setCitySearch(city?.locationName || group.destinationCity)
  }, [group.destinationCity, cities])

  const handleDeliveryTypeChange = (val: string) => {
    const name = typeShipment.find((t) => t.value === val)?.name || ''
    setDeliverySearch(name)
    const patch: Partial<ShipmentGroup> = { deliveryType: val }
    if (val !== DeliveryEnum.DELIVERY) {
      patch.latitude = null
      patch.longitude = null
    }
    if (group.importWarning) {
      patch.importWarning = undefined
    }
    onChangeGroup(patch)
  }

  // ─── Teléfono del destinatario ────────────────────────────────────────────
  // El país manda: con +58 corren las reglas venezolanas de siempre (operadora
  // o FIJO, 10/11 dígitos); con cualquier otro el número son los dígitos
  // crudos y el campo "Operadora" ni se renderiza — es un concepto que solo
  // existe acá.
  const phoneCountryCode = consigneeCountryCode(group)
  const isVEPhone = phoneCountryCode === PHONE_COUNTRY_CODE

  const countrySelectOptions = useMemo(
    () => (countryOptions.length > 0 ? countryOptions : FALLBACK_COUNTRY_OPTIONS),
    [countryOptions]
  )
  const selectedCountry = useMemo(
    () => findCountryByDialCode(countrySelectOptions, phoneCountryCode),
    [countrySelectOptions, phoneCountryCode]
  )
  // El input lleva SOLO el prefijo. `FormSearch` filtra por el texto del input,
  // así que escribir ahí la etiqueta larga hacía que el campo no encontrara su
  // propio valor al reabrir la lista. El nombre del país se ve en el
  // desplegable y en el texto para lectores de pantalla.
  //
  // Como es el código guardado tal cual, un prefijo que /Country no liste (o el
  // fallback) se muestra igual, sin borrarle en silencio al operador el país
  // que la fila realmente tiene.
  const countryFieldValue = phoneCountryCode
  const [countrySearch, setCountrySearch] = useState(countryFieldValue)
  useEffect(() => {
    setCountrySearch(countryFieldValue)
  }, [countryFieldValue])

  const phoneCode = useMemo(() => {
    if (!isVEPhone) return ''
    if (!group.consigneePhone) return ''
    return obtenerCodigoArea(group.consigneePhone)
  }, [group.consigneePhone, isVEPhone])
  const phoneNumberOnly = useMemo(
    () =>
      isVEPhone
        ? obtenerNumero(group.consigneePhone || '')
        : (group.consigneePhone || '').replace(/\D/g, ''),
    [group.consigneePhone, isVEPhone]
  )
  const [phoneCodeSearch, setPhoneCodeSearch] = useState(phoneCode)
  useEffect(() => {
    setPhoneCodeSearch(phoneCode)
  }, [phoneCode])

  const setPhoneCombined = (code: string, num: string) => {
    const cleaned = (num || '').replace(/\D/g, '')
    // Fuera de Venezuela no hay operadora que anteponer ni 0 que agregar: los
    // dígitos van tal cual (el prefijo internacional se compone al enviar).
    if (!isVEPhone) {
      onChangeGroup({
        consigneePhone: cleaned.slice(0, PHONE_MAX_LOCAL_DIGITS),
      })
      return
    }
    if (!code) {
      onChangeGroup({ consigneePhone: cleaned })
      return
    }
    // FIJO no es un prefijo: es "no elegiste operadora móvil". El campo lleva
    // los 10 dígitos completos (código de área + número), igual que en el resto
    // de las pantallas del sistema. Antes se le anteponía un 0 de marcación
    // nacional que terminaba viajando al backend como "+58 02121234567".
    if (code === 'FIJO') {
      onChangeGroup({
        consigneePhone: cleaned.slice(0, VE_LOCAL_DIGITS),
      })
      return
    }
    onChangeGroup({ consigneePhone: code + cleaned })
  }

  const phoneInputMaxLen = !isVEPhone
    ? PHONE_MAX_LOCAL_DIGITS
    : phoneCodeSearch === 'FIJO'
      ? VE_LOCAL_DIGITS
      : 7

  /**
   * Cambio de país: los dígitos se reescriben a la convención del país nuevo
   * (`changeCountryCode` quita el 0 venezolano y el prefijo que se hubiera
   * colado dentro del número), NO se borran — el operador ve lo que tenía.
   * La operadora se limpia porque solo tiene sentido en Venezuela.
   */
  const handleCountryChange = (code: string) => {
    if (!code || code === phoneCountryCode) return
    onChangeGroup(changeCountryCode(group, code))
    if (code !== PHONE_COUNTRY_CODE) setPhoneCodeSearch('')
  }

  /**
   * Pegado en el campo Teléfono. Va en `onPaste` y no en `onChange` porque el
   * input tiene `maxLength` 7/11: el navegador RECORTA un "+57 3001234567"
   * antes de que `onChange` llegue a verlo, y el país nunca se detectaba.
   */
  const handlePhonePaste = (e: any) => {
    const pasted = e.clipboardData?.getData('text') ?? ''
    if (!pasted.trim()) return
    const parts = splitPhoneCountryCode(pasted)

    // Lo pegado NO dice de qué país es: se respeta el país que la fila ya tiene.
    // En Venezuela sigue valiendo el atajo de siempre (pegar "0414 1234567" y
    // que se reparta en operadora + número); fuera de ella el `onChange` normal
    // ya limpia y recorta los dígitos, así que no hay nada que interceptar.
    if (!parts.explicitCountry) {
      if (!isVEPhone) return
      const parsed = tryParsePastedPhone(pasted, phonePrefixList)
      if (!parsed) return
      e.preventDefault()
      setPhoneCodeSearch(parsed.code)
      setPhoneCombined(parsed.code, parsed.number)
      return
    }

    // Lo pegado trae su país: `splitPhoneCountryCode` YA separó el prefijo de
    // los dígitos, así que se escriben tal cual. Pasarlos por
    // `changeCountryCode` los haría pasar de nuevo por la heurística de
    // "prefijo colado" y le comería los primeros dígitos a un número cuyo
    // local arranca igual que su prefijo (pegar "+55 55987654321" quedaba en
    // "987654321", y encima válido).
    e.preventDefault()
    onChangeGroup({
      consigneePhoneCountryCode: parts.countryCode,
      consigneePhone: parts.phone,
    })
    if (parts.countryCode !== PHONE_COUNTRY_CODE) setPhoneCodeSearch('')
  }

  const [applyingFav, setApplyingFav] = useState(false)
  const [favWarning, setFavWarning] = useState<string>('')
  const [mapsSelected, setMapsSelected] = useState<{
    name: string
    address: string
    postalCode: string
    coordinates: { lat: number; lng: number }
  }>({ name: '', address: '', postalCode: '', coordinates: { lat: 0, lng: 0 } })
  const reqIdRef = useRef(0)

  const handleSelectDestination = async (fav: DestinationInterface) => {
    const myReq = ++reqIdRef.current
    setApplyingFav(true)
    setFavWarning('')
    try {
      if (!fav.isHomeDelivery) {
        const buCodeWanted = fav.businessUnit?.buCode || fav.store?.code
        if (buCodeWanted) {
          const buMatch = businessUnits.find(
            (b: any) => b.buCode === buCodeWanted
          )
          if (buMatch && (buMatch as any).isLocked === true) {
            if (myReq === reqIdRef.current) {
              setFavWarning(
                `Tienda destino ${buCodeWanted} está bloqueada. Selecciona otro favorito.`
              )
            }
            return
          }
        }
      }
      const result = await applyDestinationFromFavorite(
        fav,
        getStoreByCoverageDjango
      )
      if (myReq !== reqIdRef.current) return
      const patch = group.importWarning
        ? { ...result.patch, importWarning: undefined }
        : result.patch
      onChangeGroup(patch)
      if (result.warning) setFavWarning(result.warning)
    } finally {
      if (myReq === reqIdRef.current) setApplyingFav(false)
    }
  }

  const handleClearFavorite = () => {
    ++reqIdRef.current
    setApplyingFav(false)
    onChangeGroup(clearConsigneeFields())
    setPhoneCodeSearch('')
    setFavWarning('')
  }

  const hasFavorite = !!group.favoriteDestinationId
  const favLockDisabled = hasFavorite || applyingFav
  const favLockClass = hasFavorite ? 'bg-gray-50' : ''

  // Domicilio: la "Dirección destino *" (requerida) se puede escribir a mano cuando NO
  // hay una dirección canónica de Maps que proteger — es decir, sin coordenadas (o con
  // coords que no geocodificaron) — y la fila no está bloqueada por un favorito. Así, si
  // el operador solo eligió ciudad, igual puede tipear la dirección. Con coordenadas +
  // dirección resuelta queda readonly (Maps es la fuente de verdad).
  const canEditAddressManually =
    !favLockDisabled && !(hasMapsCoords && !!group.destinationAddress?.trim())

  const favoriteHasInvalidData =
    hasFavorite &&
    (!!errors.consigneePhone ||
      !!errors.consigneeEmail ||
      !!errors.consigneeDocNumber ||
      !!errors.consigneeDocType ||
      !!errors.consigneeName)

  const lastLookedUpCoordsRef = useRef<string>('')
  useEffect(() => {
    if (!isHome) return
    if (group.latitude == null || group.longitude == null) return
    if (hasFavorite || applyingFav) return
    const coordKey = `${group.latitude},${group.longitude}`
    if (coordKey === lastLookedUpCoordsRef.current) return

    const myReq = ++reqIdRef.current
    const coverageController = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const [res, geo] = await Promise.all([
          getStoreByCoverageDjango(
            group.longitude as number,
            group.latitude as number,
            coverageController.signal
          ),
          reverseGeocodeCoords(
            group.latitude as number,
            group.longitude as number
          ),
        ])
        if (myReq !== reqIdRef.current) return
        if (res.didError || !res.model) {
          lastLookedUpCoordsRef.current = coordKey
          onChangeGroup({ coordWarning: COORD_OUT_OF_ZONE_MSG })
          return
        }
        lastLookedUpCoordsRef.current = coordKey
        const loc = res.model.location || {}
        onChangeGroup({
          destinationAddress: geo?.address || group.destinationAddress,
          destinationCity: loc.locationCode || group.destinationCity,
          storeCode: res.model.buCode || group.storeCode,
          postalCode: geo?.postalCode || loc.postalCode || group.postalCode,
          coordWarning: undefined,
        })
      } catch (_) {}
    }, 500)
    return () => {
      clearTimeout(timer)
      coverageController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.latitude, group.longitude, isHome, hasFavorite, applyingFav])

  const suggestedFavorite = useMemo<DestinationInterface | undefined>(() => {
    if (group.favoriteDestinationId) return undefined
    if (group.optedOutOfFavorite) return undefined
    const rif = (group.consigneeDocNumber || '').trim()
    if (!rif || !favsByRif) return undefined
    return favsByRif.get(rif)
  }, [
    group.consigneeDocNumber,
    group.favoriteDestinationId,
    group.optedOutOfFavorite,
    favsByRif,
  ])

  return (
    <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden transition-all duration-300">
      {/* Header. El checkbox de selección va FUERA del botón del acordeón, no
          adentro: un control interactivo dentro de otro no es HTML válido, el
          click se comería el toggle y con teclado el checkbox quedaría
          inalcanzable. Por eso el fondo se movió al contenedor. */}
      <div
        className={`w-full flex items-center ${
          selected ? 'bg-main-50 border-main-200' : headerBg
        }`}
      >
        {selectable && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelected?.()}
            className="ml-4 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 text-main-500 focus:ring-main-500"
            aria-label={`Seleccionar guía ${
              group.shipmentHeaderReference || '(sin tracking)'
            }`}
          />
        )}
        <button
          type="button"
          className={`flex-1 min-w-0 flex items-center justify-between py-3 text-left ${
            selectable ? 'pl-3 pr-4' : 'px-4'
          }`}
          onClick={() => {
            if (isControlled) {
              onToggle?.()
            } else {
              if (open) onCollapsed?.()
              setInternalOpen((o) => !o)
            }
          }}
          aria-expanded={open}
        >
        <div className="flex items-center gap-2 min-w-0">
          {group.coordWarning ? (
            <ExclamationTriangleIcon
              className="w-4 h-4 text-red-500 flex-shrink-0"
              aria-hidden="true"
              aria-label="Error de coordenadas"
            />
          ) : hasError || hasUnfixedServerError ? (
            <ExclamationTriangleIcon
              className="w-4 h-4 text-red-500 flex-shrink-0"
              aria-hidden="true"
            />
          ) : (
            <CheckCircleIcon
              className="w-4 h-4 text-green-600 flex-shrink-0"
              aria-hidden="true"
              aria-label="Guía válida"
            />
          )}
          <span className="font-mono font-medium text-sm truncate">
            {group.shipmentHeaderReference || '(sin tracking)'}
          </span>
          <span className="text-gray-400 text-sm hidden sm:inline">•</span>
          <span className="text-gray-600 text-sm truncate hidden sm:inline">
            {group.consigneeName || '—'}
          </span>
          {group.favoriteDestinationId && (
            <span
              className="bg-main-50 text-main-500 px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 flex-shrink-0"
              title={`Destinatario favorito aplicado: ${group.consigneeName || ''}`}
            >
              <StarIcon className="w-3 h-3" aria-hidden="true" />
              <span className="hidden md:inline">Destinatario Favorito</span>
              <span className="sr-only">
                Destinatario favorito aplicado: {group.consigneeName || ''}
              </span>
            </span>
          )}
          <span className="text-gray-400 text-sm hidden md:inline">•</span>
          <span className="text-gray-500 text-sm hidden md:inline">
            {totalPieceCount} pieza(s)
          </span>
          {/* Teléfono COMPUESTO, SOLO cuando el país no es Venezuela. Es el
              único lugar donde se ve, sin abrir la fila, que una guía quedó
              cargada con otro país — que es justo la fila que hay que revisar.
              En las venezolanas (la enorme mayoría de un archivo de 400) el
              dato no aporta nada y solo agrega ruido al escanear la lista. */}
          {!isVEPhone && group.consigneePhone && (
            <>
              <span className="text-gray-400 text-sm hidden lg:inline">•</span>
              <span className="text-sm hidden lg:inline tabular-nums text-gray-700 font-medium">
                {composeConsigneePhone(group.consigneePhone, phoneCountryCode)}
              </span>
            </>
          )}
          </div>
          {errorBadge && (
            <span
              className={`ml-2 flex-shrink-0 text-xs font-medium ${dupBadgeStyle} px-2 py-0.5 rounded`}
            >
              {errorBadge}
            </span>
          )}
        </button>
      </div>

      {/* Panel expandido */}
      {open && (
        <div className="p-4 space-y-6 bg-white">
          {/*
            Motivo por el que el backend rechazó esta guía, tal cual lo devolvió.
            Se muestra SIEMPRE que exista —aunque el campo ya esté marcado— porque
            es lo único que dice por qué se cayó; los campos marcados dicen qué
            corregir, no qué pasó.

            Se cierra a mano y no al editar la fila: el chequeo de duplicado y el
            de coordenadas disparan `onChangeGroup` solos al montar, así que
            limpiarlo con cualquier cambio lo borraría antes de que se llegue a leer.
            La excepción es `serverErrorResolved`: cuando se VERIFICA contra los
            datos actuales que el motivo ya se corrigió, se oculta solo.
          */}
          {group.serverErrorNote && !serverErrorResolved && (
            <div
              className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2"
              role="alert"
            >
              <ExclamationTriangleIcon
                className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-red-800">
                  El sistema rechazó esta guía
                </p>
                <p className="text-xs text-red-700 mt-0.5 break-words">
                  {group.serverErrorNote}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onChangeGroup({ serverErrorNote: undefined })}
                className="text-red-700 hover:text-red-900 text-xs underline flex-shrink-0"
                aria-label="Cerrar motivo del rechazo"
              >
                Cerrar
              </button>
            </div>
          )}

          {/* G.4-bis: import-time warning banner (e.g., favorite override changed deliveryType) */}
          {group.importWarning && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2"
              role="status"
              aria-live="polite"
            >
              <ExclamationTriangleIcon
                className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p className="text-xs text-amber-800 flex-1">
                {group.importWarning}
              </p>
              <button
                type="button"
                onClick={() => onChangeGroup({ importWarning: undefined })}
                className="text-amber-700 hover:text-amber-900 text-xs underline flex-shrink-0"
                aria-label="Cerrar aviso"
              >
                Cerrar
              </button>
            </div>
          )}

          {/* SECCIÓN 1: Datos del Envío */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Datos del Envío
            </h3>
            {/* Tracking — primer campo */}
            <div className="mb-4">
              <FormText
                id={`shipmentHeaderReference-${index}`}
                name={`shipmentHeaderReference-${index}`}
                label="Número de guía aliada *"
                value={group.shipmentHeaderReference}
                maxLength={30}
                onChange={(e: any) => {
                  const v = e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9-]/g, '')
                  // Marca edición manual: habilita el re-chequeo por fila del useEffect.
                  userEditedRef.current = true
                  onChangeGroup({ shipmentHeaderReference: v })
                }}
                error={errors.shipmentHeaderReference}
              />
              {errors.shipmentHeaderReference?.includes(
                'duplicado en esta carga masiva'
              ) &&
                onRequestMerge && (
                  <div className="mt-1 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={onRequestMerge}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      Combinar grupos
                    </button>
                  </div>
                )}
              {isDup && !errors.shipmentHeaderReference && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  Esta guía ya existe en el sistema. Edita el número antes de
                  enviar.
                </p>
              )}
            </div>

            {serviceOptions.length > 1 ? (
              <div className="mb-4">
                <FormSelect<ServiceDTO>
                  id={`service-${index}`}
                  name={`service-${index}`}
                  label="Servicio *"
                  selected={serviceOptions.find(
                    (s) => s.serviceID === group.serviceId
                  )}
                  options={serviceOptions}
                  optionString={(s) => s.serviceShortName}
                  onSelectOption={(s) =>
                    onChangeGroup({ serviceId: s.serviceID })
                  }
                />
              </div>
            ) : serviceOptions.length === 1 ? (
              <div className="mb-4">
                <FormText
                  id={`service-${index}`}
                  name={`service-${index}`}
                  label="Servicio"
                  value={serviceOptions[0].serviceShortName}
                  disabled
                  className="bg-gray-50"
                  onChange={() => {}}
                />
              </div>
            ) : null}

            {/* Tienda origen — va acá (después de Servicio) porque es un dato del
                ENVÍO, no del remitente: aplica igual bajo COD, donde los campos de
                remitente están bloqueados. Con varias tiendas se elige por guía; con
                una sola se muestra solo lectura (para que se vea de dónde sale la
                guía); con ninguna no se renderiza. */}
            {originBusinessUnits.length > 0 && (
              <div className="mb-4">
                {originBusinessUnits.length > 1 ? (
                  <FormSearch
                    id={`originStoreCode-${index}`}
                    name={`originStoreCode-${index}`}
                    label="Tienda origen *"
                    value={originSearch}
                    options={originBusinessUnits}
                    error={errors.originStoreCode}
                    onChange={(e: any) => setOriginSearch(e.target.value)}
                    onSelectOption={(opt: any) => {
                      onChangeGroup({ originStoreCode: opt?.buCode || '' })
                      setOriginSearch(originLabel(opt))
                    }}
                    onFilter={(opt: any, filter: string) => {
                      // Filtrar contra el LABEL ("buCode - nombre"), no campo por campo:
                      // así el query parcial matchea Y al reentrar con el label completo
                      // (value = "MIA - Miami") la opción seleccionada sigue apareciendo.
                      const q = (filter || '').toLowerCase().trim()
                      return q === '' || originLabel(opt).toLowerCase().includes(q)
                    }}
                    RenderOption={({ option }: any) => (
                      <div className="px-2 py-1 text-sm">
                        {originLabel(option)}
                      </div>
                    )}
                  />
                ) : (
                  <FormText
                    id={`originStoreCode-ro-${index}`}
                    name={`originStoreCode-ro-${index}`}
                    label="Tienda origen"
                    value={originLabel(originBuObj)}
                    disabled
                    readOnly
                    className="bg-gray-50"
                    onChange={() => {}}
                  />
                )}
              </div>
            )}

            {/* Cambio J.2: Domicilio gets explicit layout per operator's mental model:
                Row 1: Tipo de entrega + Búsqueda Google Maps (paired)
                Row 2: Coordenadas section (lat/lng, full-width gray panel)
                Row 3: Ciudad + Tienda asignada (both Maps-derived, locked)
                Row 4: Dirección destino * (readonly textarea showing canonical Maps result)
                Row 5: Código postal
                Row 6: Punto de referencia (delivery hint)
                Office keeps its original grid (Tipo + Código tienda → Dir → Postal). */}
            {isHome ? (
              <>
                {/* Row 1: Tipo de entrega + Búsqueda en Google Maps */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSearch
                    id={`deliveryType-${index}`}
                    name={`deliveryType-${index}`}
                    label="Tipo de entrega *"
                    value={deliverySearch}
                    options={typeShipment}
                    error={errors.deliveryType}
                    onChange={(e: any) => setDeliverySearch(e.target.value)}
                    onSelectOption={(opt: any) =>
                      handleDeliveryTypeChange(opt?.value || '')
                    }
                    onFilter={(opt: any, filter: string) =>
                      (opt.name || '')
                        .toLowerCase()
                        .includes(filter.toLowerCase())
                    }
                    RenderOption={({ option }: any) => (
                      <div className="px-2 py-1 text-sm">{option.name}</div>
                    )}
                    disabled={favLockDisabled}
                    className={favLockClass}
                  />
                  <FormMapsAutocomplete
                    id={`mapsSearch-${index}`}
                    name={`mapsSearch-${index}`}
                    label="Búsqueda en google maps"
                    placeholder="Buscar dirección para cambiarla…"
                    region={AutocompleteRegion.TEALCA}
                    selected={mapsSelected}
                    onSelectLocation={(loc, bu) => {
                      // Reflejar lo elegido en el campo (estado local) y aplicar al grupo.
                      setMapsSelected({
                        name: loc.address || '',
                        address: loc.address || '',
                        postalCode: loc.postalCode || '',
                        coordinates: {
                          lat: loc.coordinates?.lat ?? 0,
                          lng: loc.coordinates?.lng ?? 0,
                        },
                      })
                      onChangeGroup({
                        destinationAddress:
                          loc.address || group.destinationAddress,
                        postalCode: loc.postalCode || group.postalCode,
                        latitude: loc.coordinates?.lat ?? group.latitude,
                        longitude: loc.coordinates?.lng ?? group.longitude,
                        destinationCity:
                          bu?.location?.locationCode || group.destinationCity,
                        storeCode: bu?.buCode || group.storeCode,
                        // Una selección válida de Maps limpia cualquier aviso de coords.
                        coordWarning: undefined,
                      })
                    }}
                    disabled={favLockDisabled}
                  />
                </div>

                {/* Row 2: Coordenadas — gray panel. Editing lat/lng triggers J.1 territory lookup.
                    Per-row coordWarning (set by validateCoordsInBatches when territory check
                    fails) renders below the inputs so the operator sees the explanation right
                    where they fix it. Auto-clears on edit so the J.1 re-lookup can succeed. */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600 mb-3">
                    Coordenadas de entrega — opcional, solo para domicilio
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormText
                      id={`latitude-${index}`}
                      name={`latitude-${index}`}
                      label="Latitud"
                      value={
                        group.latitude !== null ? String(group.latitude) : ''
                      }
                      placeholder="Ej: 10.4806"
                      onChange={(e: any) => {
                        const v = e.target.value.replace(',', '.')
                        const parsed = parseFloat(v)
                        onChangeGroup({
                          latitude: isNaN(parsed) ? null : parsed,
                          ...(group.coordWarning
                            ? { coordWarning: undefined }
                            : {}),
                        })
                      }}
                      error={errors.latitude}
                      disabled={favLockDisabled}
                      className={favLockClass}
                    />
                    <FormText
                      id={`longitude-${index}`}
                      name={`longitude-${index}`}
                      label="Longitud"
                      value={
                        group.longitude !== null ? String(group.longitude) : ''
                      }
                      placeholder="Ej: -66.9147"
                      onChange={(e: any) => {
                        const v = e.target.value.replace(',', '.')
                        const parsed = parseFloat(v)
                        onChangeGroup({
                          longitude: isNaN(parsed) ? null : parsed,
                          ...(group.coordWarning
                            ? { coordWarning: undefined }
                            : {}),
                        })
                      }}
                      error={errors.longitude}
                      disabled={favLockDisabled}
                      className={favLockClass}
                    />
                  </div>
                  {group.coordWarning && (
                    <div
                      className="mt-3 flex items-start gap-2 text-xs bg-red-50 border border-red-200 rounded-md px-3 py-2"
                      role="alert"
                      aria-live="polite"
                    >
                      <ExclamationTriangleIcon
                        className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <p className="text-red-800 flex-1">
                        {group.coordWarning}
                      </p>
                    </div>
                  )}
                </div>

                {/* Row 3: Ciudad + Tienda asignada.
                    Ciudad: locked only when BOTH coords AND a store were resolved
                    by Maps/territory. If coords didn't resolve a store (territory
                    returned no BU, or only city + no store), the operator can
                    still pick a city manually — and picking the city derives the
                    storeCode from citiesByBu so the row becomes submittable. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <FormSearch
                    id={`destinationCity-${index}`}
                    name={`destinationCity-${index}`}
                    label={
                      hasMapsCoords && group.storeCode
                        ? 'Ciudad destino * (asignada por Maps)'
                        : 'Ciudad destino *'
                    }
                    value={citySearch}
                    options={cities}
                    error={errors.destinationCity}
                    disabled={
                      (hasMapsCoords && !!group.storeCode) || favLockDisabled
                    }
                    className={hasFavorite ? favLockClass : ''}
                    onChange={(e: any) => setCitySearch(e.target.value)}
                    onSelectOption={(opt: any) => {
                      const locationCode = opt?.locationCode || ''
                      // Derive storeCode from citiesByBu lookup so picking a city
                      // resolves "Tienda asignada" too — required when coords are
                      // missing or didn't yield a store.
                      const derivedBuCode =
                        citiesByBu.find((c) => c.locationCode === locationCode)
                          ?.buCode || ''
                      // Pull postal code from the resolved BU if we have one.
                      const derivedBu = derivedBuCode
                        ? businessUnits.find(
                            (b) =>
                              String(b.buCode ?? '') === String(derivedBuCode)
                          )
                        : undefined
                      onChangeGroup({
                        destinationCity: locationCode,
                        storeCode: derivedBuCode || group.storeCode,
                        postalCode:
                          derivedBu?.location?.postalCode || group.postalCode,
                      })
                      setCitySearch(opt?.locationName || '')
                    }}
                    onFilter={(opt: any, filter: string) =>
                      (opt.locationName || '')
                        .toLowerCase()
                        .includes(filter.toLowerCase())
                    }
                    RenderOption={({ option }: any) => (
                      <div className="px-2 py-1 text-sm">
                        {option.locationName}
                      </div>
                    )}
                  />
                  <FormText
                    id={`assignedStore-${index}`}
                    name={`assignedStore-${index}`}
                    label="Tienda asignada"
                    value={
                      buObj
                        ? `${buObj.buCode} - ${buObj.location?.name || buObj.buName || (buObj as any).buArea || ''}`
                            .replace(/^ - /, '')
                            .trim()
                        : group.storeCode || ''
                    }
                    disabled
                    placeholder="Se asignará automáticamente por las coordenadas / ciudad"
                    className="bg-gray-50"
                    onChange={() => {}}
                  />
                </div>

                {/* Row 4: Dirección destino — dirección canónica de Maps (readonly cuando
                    hay coordenadas) o entrada manual cuando solo se eligió ciudad. */}
                <div className="mt-4">
                  <FormTextArea
                    id={`destinationAddress-${index}`}
                    name={`destinationAddress-${index}`}
                    label="Dirección destino *"
                    value={group.destinationAddress || ''}
                    disabled={!canEditAddressManually}
                    className={canEditAddressManually ? '' : 'bg-gray-50'}
                    placeholder={
                      canEditAddressManually
                        ? 'Escribe la dirección de entrega'
                        : 'Selecciona una dirección en Google Maps o ingresa coordenadas'
                    }
                    error={errors.destinationAddress}
                    onChange={(e: any) =>
                      onChangeGroup({ destinationAddress: e.target.value })
                    }
                  />
                </div>

                {/* Row 5: Código postal */}
                <div className="mt-4">
                  <FormText
                    id={`postalCode-${index}`}
                    name={`postalCode-${index}`}
                    label="Código postal"
                    value={group.postalCode}
                    onChange={(e: any) =>
                      onChangeGroup({ postalCode: e.target.value })
                    }
                    disabled={favLockDisabled}
                    className={favLockClass}
                  />
                </div>

                {/* Row 6: Punto de referencia — delivery hint for the courier */}
                <div className="mt-4">
                  <FormText
                    id={`addressReference-${index}`}
                    name={`addressReference-${index}`}
                    label="Punto de referencia"
                    placeholder='Ej: "Frente al parque", "Portón negro"'
                    value={group.addressReference || ''}
                    onChange={(e: any) =>
                      onChangeGroup({ addressReference: e.target.value })
                    }
                    disabled={favLockDisabled}
                    className={favLockClass}
                  />
                </div>
              </>
            ) : (
              /* Office: original grid layout (Tipo + Código tienda → Dirección → Postal) */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormSearch
                  id={`deliveryType-${index}`}
                  name={`deliveryType-${index}`}
                  label="Tipo de entrega *"
                  value={deliverySearch}
                  options={typeShipment}
                  error={errors.deliveryType}
                  onChange={(e: any) => setDeliverySearch(e.target.value)}
                  onSelectOption={(opt: any) =>
                    handleDeliveryTypeChange(opt?.value || '')
                  }
                  onFilter={(opt: any, filter: string) =>
                    (opt.name || '')
                      .toLowerCase()
                      .includes(filter.toLowerCase())
                  }
                  RenderOption={({ option }: any) => (
                    <div className="px-2 py-1 text-sm">{option.name}</div>
                  )}
                  disabled={favLockDisabled}
                  className={favLockClass}
                />
                <FormSearch
                  id={`storeCode-${index}`}
                  name={`storeCode-${index}`}
                  label="Código de tienda *"
                  value={buSearch}
                  options={destinationOptions ?? businessUnits}
                  error={errors.storeCode}
                  onChange={(e: any) => setBuSearch(e.target.value)}
                  onSelectOption={(opt: any) => {
                    const loc = opt?.location || {}
                    onChangeGroup({
                      storeCode: opt?.buCode || '',
                      destinationCity: loc.locationCode || '',
                      destinationAddress:
                        loc.address || group.destinationAddress,
                      postalCode: loc.postalCode || group.postalCode,
                    })
                    setBuSearch(
                      opt
                        ? `${opt.buCode || ''} - ${loc.name || opt.buName || ''}`
                            .replace(/^ - /, '')
                            .trim()
                        : ''
                    )
                  }}
                  onFilter={(opt: any, filter: string) => {
                    // Filtrar contra el LABEL ("buCode - nombre"), no campo por campo:
                    // al reentrar, el value es el label completo y filterByName no lo
                    // matcheaba → "Elementos no encontrados". Mismo fix que Oficina origen.
                    const q = (filter || '').toLowerCase().trim()
                    if (q === '') return true
                    const label = `${opt.buCode || ''} - ${
                      opt.location?.name || opt.buName || opt.buArea || ''
                    }`.toLowerCase()
                    return label.includes(q)
                  }}
                  RenderOption={({ option }: any) => (
                    <div className="px-2 py-1 text-sm">
                      <p className="font-medium">
                        {option.buCode} —{' '}
                        {option.location?.name || option.buName}
                      </p>
                      {option.location?.address && (
                        <p className="text-xs text-gray-500">
                          {option.location.address}
                        </p>
                      )}
                    </div>
                  )}
                  disabled={favLockDisabled}
                  className={favLockClass}
                />
                <FormText
                  id={`destinationAddress-${index}`}
                  name={`destinationAddress-${index}`}
                  label="Dirección destino *"
                  value={group.destinationAddress}
                  onChange={(e: any) =>
                    onChangeGroup({ destinationAddress: e.target.value })
                  }
                  error={errors.destinationAddress}
                  // En retiro por tienda la dirección la pone la tienda elegida
                  // (`onSelectOption` del código de tienda la copia del catálogo),
                  // así que normalmente es de solo lectura. PERO hay tiendas cuyo
                  // registro NO trae dirección: ahí el campo quedaba vacío,
                  // deshabilitado y marcado "Requerido" a la vez, o sea una fila
                  // imposible de enviar y sin ninguna acción en pantalla para
                  // destrabarla. Cuando la tienda no aporta dirección, se habilita
                  // la carga manual.
                  disabled={storeProvidesAddress}
                  className={storeProvidesAddress ? 'bg-gray-50' : ''}
                  placeholder={
                    storeProvidesAddress
                      ? ''
                      : group.storeCode?.trim()
                        ? 'La tienda elegida no tiene dirección registrada — escríbela'
                        : 'Se completa al elegir la tienda; si no trae dirección, escríbela'
                  }
                />
                <FormText
                  id={`postalCode-${index}`}
                  name={`postalCode-${index}`}
                  label="Código postal"
                  value={group.postalCode}
                  onChange={(e: any) =>
                    onChangeGroup({ postalCode: e.target.value })
                  }
                  disabled={favLockDisabled}
                  className={favLockClass}
                />
              </div>
            )}

            <div className="mt-4">
              <FormTextArea
                id={`notes-${index}`}
                name={`notes-${index}`}
                label="Observaciones"
                value={group.notes}
                onChange={(e: any) => onChangeGroup({ notes: e.target.value })}
              />
            </div>
          </div>

          {/* SECCIÓN 2: Remitente — oculto en COD */}
          {!isCOD && (
            <div>
              <hr className="my-3"></hr>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                Remitente
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormSearch
                  id={`shipperDocType-${index}`}
                  name={`shipperDocType-${index}`}
                  label="Tipo de documento *"
                  value={shipperDocSearch}
                  options={identificationTypes}
                  error={errors.shipperDocType}
                  onChange={(e: any) => setShipperDocSearch(e.target.value)}
                  onSelectOption={(opt: any) => {
                    onChangeGroup({
                      shipperDocType:
                        opt?.taxIdentificationTypeId?.toString() || '',
                    })
                    setShipperDocSearch(opt?.abbreviationName || '')
                  }}
                  onFilter={(opt: any, filter: string) =>
                    filterByName(opt, filter)
                  }
                  RenderOption={({ option }: any) => (
                    <div className="px-2 py-1 text-sm">
                      {option.abbreviationName}
                    </div>
                  )}
                />
                <FormText
                  id={`shipperDocNumber-${index}`}
                  name={`shipperDocNumber-${index}`}
                  label="Nº Documento *"
                  value={group.shipperDocNumber}
                  onChange={(e: any) => {
                    const v = e.target.value.replace(/\D/g, '')
                    onChangeGroup({ shipperDocNumber: v })
                  }}
                  error={errors.shipperDocNumber}
                />
                <FormText
                  id={`shipperName-${index}`}
                  name={`shipperName-${index}`}
                  label="Nombre *"
                  value={group.shipperName}
                  onChange={(e: any) =>
                    onChangeGroup({ shipperName: e.target.value })
                  }
                  error={errors.shipperName}
                />
              </div>
            </div>
          )}

          {/* SECCIÓN 3: Destinatario */}
          <div>
            <hr className="my-3"></hr>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Destinatario
            </h3>

            {/* V3+E: Favorite picker (rich UI) */}
            <div className="mb-4">
              <FavoriteDestinationPicker
                id={`fav-picker-${index}`}
                favorites={favoriteDestinations}
                selectedId={group.favoriteDestinationId}
                selectedDisplay={group.consigneeName}
                disabled={applyingFav}
                loading={applyingFav}
                onSelect={handleSelectDestination}
                onClear={handleClearFavorite}
              />
              {favWarning && (
                <p
                  className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1"
                  role="status"
                  aria-live="polite"
                >
                  <ExclamationTriangleIcon
                    className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <span>{favWarning}</span>
                </p>
              )}
            </div>

            {/* V3: RIF auto-suggest banner */}
            {suggestedFavorite && !applyingFav && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3 flex items-center justify-between text-xs gap-2 flex-wrap">
                <span className="text-amber-800 flex items-center gap-1 flex-1 min-w-0">
                  <StarIcon className="w-3.5 h-3.5 flex-shrink-0 text-amber-600" />
                  <span className="truncate">
                    Coincide con favorito:{' '}
                    <strong>
                      {suggestedFavorite.account?.accountFullName}
                    </strong>
                  </span>
                </span>
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-800 font-medium px-3 py-2 rounded hover:bg-amber-100"
                  onClick={() => handleSelectDestination(suggestedFavorite)}
                >
                  Aplicar favorito
                </button>
              </div>
            )}

            {/* H.3c: when favorite is applied, lock destinatario fields to keep them
                in sync with the stored favorite. Operator clears the favorite via the
                picker's X button to unlock. */}
            {hasFavorite && !favoriteHasInvalidData && (
              <div
                className="mb-3 text-xs text-gray-600 flex items-center gap-2"
                role="status"
              >
                <StarIcon
                  className="w-3.5 h-3.5 text-main-500 flex-shrink-0"
                  aria-hidden="true"
                />
                <span>
                  No se puede modificar la información porque son datos de un
                  destinatario favorito. Para editar, limpia la selección con el
                  botón ×.
                </span>
              </div>
            )}

            {/* Favorito con datos inválidos para el sistema (ej. teléfono que no
                cumple el formato del backend). Los campos están bloqueados a propósito:
                editarlos acá NO actualiza el favorito. Le indicamos al operador que lo
                corrija en su libreta de Destinos favoritos, o que lo limpie (×) para
                cargar un valor solo para esta guía. */}
            {favoriteHasInvalidData && (
              <div
                className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2"
                role="alert"
              >
                <ExclamationTriangleIcon
                  className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div className="text-xs text-red-800 flex-1 space-y-1">
                  <p className="font-semibold">
                    Este destinatario favorito tiene datos inválidos
                    {errors.consigneePhone ? ' (teléfono)' : ''}.
                  </p>
                  {errors.consigneePhone && <p>{errors.consigneePhone}</p>}
                  <p>
                    Modificá el favorito en <strong>Destinos favoritos</strong>{' '}
                    antes de cargarlo, o limpiá la selección con el botón{' '}
                    <strong>×</strong> para ingresar otro número solo en esta
                    guía (no se guardará en el favorito).
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormSearch
                id={`consigneeDocType-${index}`}
                name={`consigneeDocType-${index}`}
                label="Tipo de documento *"
                value={consigneeDocSearch}
                options={identificationTypes}
                error={errors.consigneeDocType}
                onChange={(e: any) => setConsigneeDocSearch(e.target.value)}
                onSelectOption={(opt: any) => {
                  onChangeGroup({
                    consigneeDocType:
                      opt?.taxIdentificationTypeId?.toString() || '',
                  })
                  setConsigneeDocSearch(opt?.abbreviationName || '')
                }}
                onFilter={(opt: any, filter: string) =>
                  filterByName(opt, filter)
                }
                RenderOption={({ option }: any) => (
                  <div className="px-2 py-1 text-sm">
                    {option.abbreviationName}
                  </div>
                )}
                disabled={favLockDisabled}
                className={favLockClass}
              />
              <FormText
                id={`consigneeDocNumber-${index}`}
                name={`consigneeDocNumber-${index}`}
                label="Nº Documento *"
                value={group.consigneeDocNumber}
                onChange={(e: any) => {
                  const v = e.target.value.replace(/\D/g, '')
                  onChangeGroup({ consigneeDocNumber: v })
                }}
                error={errors.consigneeDocNumber}
                disabled={favLockDisabled}
                className={favLockClass}
              />
              <FormText
                id={`consigneeName-${index}`}
                name={`consigneeName-${index}`}
                label="Nombre *"
                value={group.consigneeName}
                onChange={(e: any) =>
                  onChangeGroup({ consigneeName: e.target.value })
                }
                error={errors.consigneeName}
                disabled={favLockDisabled}
                className={favLockClass}
              />
              <FormText
                id={`consigneeEmail-${index}`}
                name={`consigneeEmail-${index}`}
                label="Correo"
                value={group.consigneeEmail}
                onChange={(e: any) =>
                  onChangeGroup({ consigneeEmail: e.target.value })
                }
                error={errors.consigneeEmail}
                disabled={favLockDisabled}
                className={favLockClass}
              />
              {/* Teléfono: País + Operadora (solo Venezuela) + número. Ocupa dos
                  columnas porque son tres campos; los anchos son fijos para que
                  ocultar Operadora al elegir otro país no mueva el número de
                  sitio. En móvil País y Operadora comparten una línea.
                  Va ÚLTIMO porque al ocupar dos columnas no puede compartir fila
                  con un campo simple en la grilla de 2 columnas (tablet): puesto
                  antes del Correo dejaba dos filas a medio llenar. */}
              <div className="md:col-span-2">
                <div className="flex flex-wrap md:flex-nowrap gap-2">
                  <div
                    className={
                      isVEPhone
                        ? 'w-[calc(50%-0.25rem)] md:w-44 min-w-0'
                        : 'w-full md:w-44 min-w-0'
                    }
                  >
                    <FormSearch<CountryPhoneOption>
                      id={`consigneePhoneCountry-${index}`}
                      name={`consigneePhoneCountry-${index}`}
                      label="País *"
                      value={countrySearch}
                      options={countrySelectOptions}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      onSelectOption={(opt) => {
                        setCountrySearch(opt?.dialCode || '')
                        handleCountryChange(opt?.dialCode || '')
                      }}
                      // Escribir en el buscador NO cambia el país: si el
                      // operador teclea "Colombia" y se va sin elegir de la
                      // lista, el campo se restaura al país que la fila tiene
                      // de verdad. Sin esto el combo quedaba mostrando un país
                      // y la guía se enviaba con otro — acá el valor guardado
                      // es el ÚNICO portador del país, no hay un dígito visible
                      // que delate la diferencia.
                      onChangeFocus={(focus: boolean) => {
                        if (!focus) setCountrySearch(countryFieldValue)
                      }}
                      onFilter={filterCountryOption}
                      RenderOption={({ option }) => (
                        <div className="px-2 py-1 text-sm">{option.label}</div>
                      )}
                      disabled={favLockDisabled}
                      className={favLockClass}
                    />
                  </div>
                  {isVEPhone && (
                    <div className="w-[calc(50%-0.25rem)] md:w-28 min-w-0">
                      <FormSearch
                        id={`consigneePhoneCode-${index}`}
                        name={`consigneePhoneCode-${index}`}
                        label="Operadora *"
                        value={phoneCodeSearch}
                        options={phonePrefixList}
                        onChange={(e: any) => setPhoneCodeSearch(e.target.value)}
                        onSelectOption={(opt: any) => {
                          const code = opt?.value || ''
                          setPhoneCodeSearch(code)
                          setPhoneCombined(code, phoneNumberOnly)
                        }}
                        onFilter={(opt: any, filter: string) =>
                          (opt.value || '')
                            .toLowerCase()
                            .includes(filter.toLowerCase()) ||
                          (opt.text || '')
                            .toLowerCase()
                            .includes(filter.toLowerCase())
                        }
                        RenderOption={({ option }: any) => (
                          <div className="px-2 py-1 text-sm">{option.value}</div>
                        )}
                        disabled={favLockDisabled}
                        className={favLockClass}
                      />
                    </div>
                  )}
                  <div className="w-full md:flex-1 md:min-w-[10rem]">
                    <FormText
                      id={`consigneePhone-${index}`}
                      name={`consigneePhone-${index}`}
                      label={
                        isVEPhone ? 'Teléfono *' : 'Teléfono (sin código de país) *'
                      }
                      value={phoneNumberOnly}
                      maxLength={phoneInputMaxLen}
                      inputMode="numeric"
                      aria-describedby={`consigneePhoneCountryHint-${index}`}
                      placeholder={
                        !isVEPhone
                          ? '3001234567'
                          : phoneCodeSearch === 'FIJO'
                            ? '2121234567'
                            : '1234567'
                      }
                      onPaste={handlePhonePaste}
                      onChange={(e: any) => {
                        const raw = e.target.value
                        // El atajo de "pegar el número completo" (0414 1234567 →
                        // operadora + 7) es venezolano; fuera de Venezuela el
                        // número entero son dígitos válidos y partirlo lo rompe.
                        if (isVEPhone) {
                          const parsed = tryParsePastedPhone(
                            raw,
                            phonePrefixList
                          )
                          if (parsed) {
                            setPhoneCodeSearch(parsed.code)
                            setPhoneCombined(parsed.code, parsed.number)
                            return
                          }
                        }
                        const v = raw
                          .replace(/\D/g, '')
                          .slice(0, phoneInputMaxLen)
                        setPhoneCombined(phoneCodeSearch, v)
                      }}
                      error={errors.consigneePhone}
                      disabled={favLockDisabled}
                      className={favLockClass}
                    />
                  </div>
                </div>
                {/* El país está en un combo aparte: sin esto un lector de
                    pantalla lee "Teléfono" sin decir a qué país corresponde. */}
                <span
                  id={`consigneePhoneCountryHint-${index}`}
                  className="sr-only"
                >
                  {`País del teléfono: ${
                    selectedCountry?.countryName || phoneCountryCode
                  } (${phoneCountryCode}). Escribe el número sin el código de país.`}
                </span>
                {countryCatalogFailed && (
                  <p className="mt-1 text-xs text-gray-500">
                    {isVEPhone
                      ? `No se pudo cargar la lista de países. Solo se puede elegir Venezuela (${PHONE_COUNTRY_CODE}).`
                      : // La fila YA trae un país del archivo: decirle que solo
                        // puede elegir Venezuela contradice lo que está viendo.
                        `No se pudo cargar la lista de países: esta guía conserva el código ${phoneCountryCode} del archivo, pero no se puede cambiar.`}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* SECCIÓN 4: Piezas */}
          <div>
            <hr className="my-4"></hr>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Piezas ({totalPieceCount})
            </h3>
            {errors.pieces && (
              <p className="text-sm text-red-600 mb-2">{errors.pieces}</p>
            )}
            <ShipmentBulkUploadPiecesList
              groupIndex={index}
              pieces={group.pieces}
              pieceCategories={pieceCategories}
              onChangePiece={onChangePiece}
              onAddPiece={onAddPiece}
              onRemovePiece={onRemovePiece}
              validatePiece={(p, replaceIndex) =>
                // Las piezas del grupo + el índice de la editada permiten evaluar
                // el peso a facturar de la GUÍA con el borrador ya sustituido.
                validatePiece(p, group.serviceId, group.pieces, replaceIndex)
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}
